import * as React from "react"

import * as Popover from "@radix-ui/react-popover"
import * as SelectPrimitive from "@radix-ui/react-select"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../cn"
import { useScrollLockExemption } from "../../hooks/useScrollLockExemption"
import { ChevronDownIcon } from "../ChevronDownIcon/ChevronDownIcon"

const selectTriggerVariants = cva(
  [
    "flex h-control w-full cursor-pointer items-center justify-between gap-2 rounded-md px-2",
    "text-sm text-coval-text placeholder:text-coval-text-muted",
    "transition-colors duration-150",
    "disabled:cursor-default disabled:opacity-50",
  ],
  {
    variants: {
      variant: {
        default: [
          "bg-surface-default",
          "border border-surface-1",
          "hover:bg-surface-contrast disabled:hover:bg-surface-default",
          "focus:border-coval-border-active",
        ],
        secondary: [
          "bg-surface-default",
          "border border-coval-border",
          "hover:bg-surface-contrast hover:border-coval-border-active",
          "disabled:hover:bg-surface-default disabled:hover:border-coval-border",
          "focus:border-coval-border-active",
        ],
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

const selectContentVariants = cva([
  "relative z-50 max-h-72 min-w-[var(--radix-select-trigger-width)] rounded-md",
  "bg-surface-default text-coval-text",
  "border border-coval-border shadow-md",
])

/* ── Root ──────────────────────────────────────────────────── */

const Select = SelectPrimitive.Root
const SelectGroup = SelectPrimitive.Group
const SelectValue = SelectPrimitive.Value

/* ── Trigger ───────────────────────────────────────────────── */

export interface SelectTriggerProps
  extends
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>,
    VariantProps<typeof selectTriggerVariants> {
  error?: boolean
  /**
   * Drops the chevron, for triggers whose value is short enough that the
   * affordance costs more room than it earns.
   */
  hideIcon?: boolean
}

const SelectTrigger = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ className, variant, error, hideIcon, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      selectTriggerVariants({ variant }),
      error &&
        "bg-coval-danger-surface text-coval-danger-on-surface hover:bg-coval-danger-surface disabled:hover:bg-coval-danger-surface",
      className,
    )}
    {...props}
  >
    {/* The value takes the room the chevron leaves and ellipsizes there: a
        long option must not wrap the trigger onto a second line, which would
        break the control's fixed height and the row it sits in. */}
    <span className="min-w-0 flex-1 truncate text-left">{children}</span>
    {!hideIcon && (
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon />
      </SelectPrimitive.Icon>
    )}
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

/* ── Content ───────────────────────────────────────────────── */

const SelectContent = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        selectContentVariants(),
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1",
        className,
      )}
      position={position}
      {...props}
    >
      <SelectPrimitive.Viewport
        className={cn(
          "overflow-y-auto p-1",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

/* ── Label ─────────────────────────────────────────────────── */

const SelectLabel = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      "px-2 py-1.5 text-sm font-medium text-coval-text-muted",
      className,
    )}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

/* ── Item ──────────────────────────────────────────────────── */

export interface SelectItemProps extends React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Item
> {
  description?: React.ReactNode
  icon?: React.ReactNode
}

const SelectItem = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Item>,
  SelectItemProps
>(({ className, children, description, icon, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-pointer select-none rounded-sm py-1.5 px-2",
      "text-sm text-coval-text",
      "outline-none",
      "hover:bg-surface-contrast focus:bg-surface-contrast",
      "data-[state=checked]:bg-surface-contrast",
      "data-[disabled]:cursor-default data-[disabled]:opacity-50 data-[disabled]:hover:bg-transparent",
      description ? "items-start gap-2" : "items-center gap-2",
      className,
    )}
    {...props}
  >
    {icon && (
      <span className="flex shrink-0 items-center text-coval-text-muted">
        {icon}
      </span>
    )}
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      {description && (
        <span className="text-xs text-coval-text-muted leading-snug">
          {description}
        </span>
      )}
    </div>
    <SelectPrimitive.ItemIndicator className="ml-auto shrink-0">
      <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
        <CheckIcon />
      </span>
    </SelectPrimitive.ItemIndicator>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

/* ── Separator ─────────────────────────────────────────────── */

const SelectSeparator = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-surface-1-hover", className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

/* ── SelectDropdown (options-based, supports searchable) ──── */

const selectDropdownContentVariants = cva([
  "z-50 min-w-[var(--radix-popover-trigger-width)] rounded-md p-1",
  "bg-surface-default text-coval-text",
  "border border-coval-border shadow-md",
])

export interface SelectDropdownOption {
  value: string
  label: string
  description?: React.ReactNode
  disabled?: boolean
  testId?: string
  dataAttributes?: {
    [attributeName: `data-${string}`]: string | undefined
  }
}

type AccessibleName =
  | { "aria-label": string; "aria-labelledby"?: string }
  | { "aria-label"?: string; "aria-labelledby": string }

export type SelectDropdownProps = VariantProps<typeof selectTriggerVariants> &
  AccessibleName & {
    options: SelectDropdownOption[]
    value?: string
    defaultValue?: string
    onValueChange?: (value: string) => void
    placeholder?: string
    searchable?: boolean
    searchPlaceholder?: string
    error?: boolean
    disabled?: boolean
    className?: string
    "data-testid"?: string
    searchInputTestId?: string
  }

function SelectDropdown({
  options,
  value: controlledValue,
  defaultValue,
  onValueChange,
  placeholder = "Select...",
  searchable = false,
  searchPlaceholder = "Search...",
  variant,
  error,
  disabled,
  className,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "data-testid": dataTestId,
  searchInputTestId,
}: SelectDropdownProps) {
  const [internalValue, setInternalValue] = React.useState<string | undefined>(
    defaultValue,
  )
  const [search, setSearch] = React.useState("")
  const [open, setOpen] = React.useState(false)
  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const scrollableListRef = useScrollLockExemption<HTMLDivElement>()
  const listboxId = React.useId()

  const isControlled = controlledValue !== undefined
  const selected = isControlled ? controlledValue : internalValue

  const handleSelect = React.useCallback(
    (optionValue: string) => {
      if (!isControlled) {
        setInternalValue(optionValue)
      }
      onValueChange?.(optionValue)
      setSearch("")
      setOpen(false)
    },
    [isControlled, onValueChange],
  )

  const displayText = React.useMemo(() => {
    if (!selected) {
      return null
    }
    return options.find((o) => o.value === selected)?.label ?? null
  }, [selected, options])

  const filteredOptions = React.useMemo(() => {
    if (!searchable || search.trim() === "") {
      return options
    }
    const query = search.toLowerCase()
    return options.filter((o) => o.label.toLowerCase().includes(query))
  }, [options, search, searchable])

  const handleOpenChange = React.useCallback((nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setSearch("")
    }
  }, [])

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild disabled={disabled}>
        <button
          type="button"
          role="combobox"
          disabled={disabled}
          aria-invalid={error || undefined}
          aria-expanded={open}
          aria-controls={listboxId}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          data-testid={dataTestId}
          className={cn(
            selectTriggerVariants({ variant }),
            error &&
              "bg-coval-danger-surface text-coval-danger-on-surface hover:bg-coval-danger-surface disabled:hover:bg-coval-danger-surface",
            className,
          )}
        >
          <span className="truncate">
            {displayText ?? (
              <span className="text-coval-text-muted">{placeholder}</span>
            )}
          </span>
          <ChevronDownIcon />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className={cn(selectDropdownContentVariants())}
          sideOffset={4}
          align="start"
          onOpenAutoFocus={(e) => {
            if (searchable) {
              e.preventDefault()
              searchInputRef.current?.focus()
            }
          }}
        >
          {searchable && (
            <div className="p-1">
              <div className="flex items-center gap-1.5 rounded-md border border-surface-1 bg-surface-default px-2 focus-within:border-coval-border-active">
                <SearchIcon />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  data-testid={searchInputTestId}
                  className="h-control w-full bg-transparent text-sm text-coval-text placeholder:text-coval-text-muted focus:outline-none"
                />
              </div>
            </div>
          )}
          <div
            ref={scrollableListRef}
            id={listboxId}
            className="max-h-60 overflow-y-auto"
            role="listbox"
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
          >
            {filteredOptions.length === 0 ? (
              <p className="px-2 py-4 text-center text-sm text-coval-text-muted">
                No results
              </p>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selected === option.value
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    disabled={option.disabled}
                    {...option.dataAttributes}
                    data-testid={option.testId}
                    onClick={() => handleSelect(option.value)}
                    className={cn(
                      "relative flex w-full cursor-pointer select-none rounded-sm py-1.5 px-2",
                      "text-sm text-coval-text",
                      "hover:bg-surface-contrast disabled:hover:bg-transparent",
                      isSelected && "bg-surface-contrast",
                      option.disabled && "cursor-default opacity-50",
                      option.description
                        ? "items-start gap-2"
                        : "items-center gap-2",
                    )}
                  >
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
                      <span className="truncate">{option.label}</span>
                      {option.description && (
                        <span className="text-xs leading-snug text-coval-text-muted">
                          {option.description}
                        </span>
                      )}
                    </div>
                    {isSelected && (
                      <span className="ml-auto flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                        <CheckIcon />
                      </span>
                    )}
                  </button>
                )
              })
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

SelectDropdown.displayName = "SelectDropdown"

/* ── Inline icons (avoids lucide peer-dep in @coval/ui) ──── */

function SearchIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="shrink-0 text-coval-text-muted"
    >
      <path
        d="M5.5 9.5C7.7091 9.5 9.5 7.7091 9.5 5.5C9.5 3.29086 7.7091 1.5 5.5 1.5C3.29086 1.5 1.5 3.29086 1.5 5.5C1.5 7.7091 3.29086 9.5 5.5 9.5Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.5 10.5L8.32 8.32"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M2.5 6L5 8.5L9.5 3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/* ── Exports ───────────────────────────────────────────────── */

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectDropdown,
  selectTriggerVariants,
  selectContentVariants,
  selectDropdownContentVariants,
}
